import React, { useEffect, useState } from 'react';

// Components
import { Step } from '../../components/Step';
import { Layout } from '../../components/Layout';

// Interfaces
import { Question, QuestionStatus } from '../../interfaces/Question';

// Context
import { useForm } from '../../context/form-context/hooks';
import {
  setAnswersToQuestions,
  setFormScene,
  setFormViewState,
} from '../../context/form-context/actions';

export const FormView: React.FC = () => {

  // Global form state
  const { state, dispatch } = useForm();

  // View states
  const [currentStep, setCurrentStep] = useState<number>(state.formViewState.currentStep);
  const [questions, updateQuestions] = useState<Question[]>(state.formViewState.questionnaire);
  const [previousQuestionsUpdates, setPreviousQuestionUpdates] = useState<Question[][]>(state.formViewState.previousQuestionnaires);

  useEffect(() => {
    const firstQuestion = questions.find(
      (question) => question.status === QuestionStatus.NotAnswered
    );
    const initialQuestion = firstQuestion ? firstQuestion.id : currentStep;
    setCurrentStep(initialQuestion);
  }, [state.formViewState.questionnaire]);

  const handleOnNextClick = () => {
    // Clone current state for questions
    const deepClone = JSON.parse(JSON.stringify(questions));
    setPreviousQuestionUpdates([...previousQuestionsUpdates, deepClone]);

    // Calculate if there is any follow up questions to take into consideration
    checkFollowUpQuestions(questions);
  };

  const handleOnPreviousClick = (previousId: number | undefined) => {
    if (previousId) {
      // Deep clone the previous questions states (multidimensional array)
      const previousQuestionsUpdatesClone = JSON.parse(
        JSON.stringify(previousQuestionsUpdates)
      );

      // Clone last questions array within the previous questions state
      const lastPreviousQuestionsUpdates = JSON.parse(
        JSON.stringify(
          previousQuestionsUpdatesClone[
            previousQuestionsUpdatesClone.length - 1
          ]
        )
      );

      // Update previous questions state without the last array
      previousQuestionsUpdatesClone.pop();
      setPreviousQuestionUpdates(previousQuestionsUpdatesClone);

      // Set the last questions array to the current state
      updateQuestions(lastPreviousQuestionsUpdates);

      // Navigate to previous step with the correct questions state
      setCurrentStep(previousId);
    } else {
      if (dispatch) {
        dispatch(setAnswersToQuestions(questions));
        dispatch(
          setFormViewState({
            currentStep: currentStep,
            questionnaire: questions,
            previousQuestionnaires: previousQuestionsUpdates,
          })
        );
        dispatch(setFormScene('ALREADY-A-MEMBER'))
      }
    }
  };

  const checkFollowUpQuestions = (questions: Question[]) => {
    // Keep an memory of which is the next question we should do
    let nextQuestionId: number[] = [];
    let ignoreQuestionId: number[] = [];

    questions.forEach((question) => {
      const { status, possibleAnswers, givenAnswer } = question;

      // Check if a question is answered
      if (status === QuestionStatus.Answered && possibleAnswers) {
        // Go through all possible answers and return any answered questions that also have a follow up question
        const nextQuestionsIds = possibleAnswers
          .map((answer) => {
            // If a given answer is equal to the answers value and it also has a followQuestion then save it
            if (answer.value === givenAnswer && answer.followQuestion) {
              return answer.followQuestion;
            }

            return null;
          })
          .filter((val) => val !== null) as number[]; // Remove any null results

        // Save the id of the followQuestion to our "global" variable
        nextQuestionId = nextQuestionId.concat(nextQuestionsIds);

        // Go through all possible answers and return any answered questions that also have a ignore question
        const ignoreQuestionsIds = possibleAnswers
          .map((answer) => {
            // If a given answer is equal to the answers value and it also has a ignoreQuestion then save it
            if (answer.value === givenAnswer && answer.ignoreQuestion) {
              return answer.ignoreQuestion;
            }

            return null;
          })
          .filter((val) => val !== null) as number[];

        ignoreQuestionId = ignoreQuestionId.concat(ignoreQuestionsIds);
      }

      return question;
    });

    const updatedQuestions = questions.map((question) => {
      const { status, id } = question;
      // If the question is the same as the id of what should be the next question and
      // its status is skip then change it to "Not answered"
      if (
        nextQuestionId.includes(id) &&
        status === QuestionStatus.Skip &&
        !ignoreQuestionId.includes(id)
      ) {
        question.status = QuestionStatus.NotAnswered;
      }

      // If the question has the same id of what question should be ignore then
      // update its status in order for us to skip it
      if (ignoreQuestionId.includes(id) && status !== QuestionStatus.Skip) {
        question.status = QuestionStatus.Skip;
      }
      return question;
    });

    calculateNextStep(updatedQuestions);
  };

  const handleOnChange = (q: Question) => {
    // Update status for the new question
    const updatedQuestion = {
      ...q,
      status: QuestionStatus.Answered,
    };

    // Find the index of the updated questions
    const index = questions.findIndex((q) => q.id === updatedQuestion.id);

    // make final new array of objects by combining updated object.
    const updatedQuestions = [
      ...questions.slice(0, index),
      updatedQuestion,
      ...questions.slice(index + 1),
    ];

    // Update the state with the updated questions
    updateQuestions(updatedQuestions);
  };

  const handleOnComplementaryAnswersChange = (updatedQuestion: Question) => {
    // Find the index of the updated questions
    const index = questions.findIndex((q) => q.id === updatedQuestion.id);

    // make final new array of objects by combining updated object.
    const updatedQuestions = [
      ...questions.slice(0, index),
      updatedQuestion,
      ...questions.slice(index + 1),
    ];

    // Update the state with the updated questions
    updateQuestions(updatedQuestions);
  };

  const calculateNextStep = (questions: Question[]) => {
    // Find all questions that has not been answered
    const nextQuestion = questions.find((step) => {
      return step.status === QuestionStatus.NotAnswered;
    });

    if (nextQuestion) {
      // Add current step id to next question in case user wants to go back in form
      const index = questions.findIndex((q) => q.id === nextQuestion.id);
      const updatedQuestion: Question = {
        ...nextQuestion,
        previousId: currentStep,
      };
      const updatedQuestions = [
        ...questions.slice(0, index),
        updatedQuestion,
        ...questions.slice(index + 1),
      ];
      updateQuestions(updatedQuestions);
    } else {
      updateQuestions(questions);
    }

    // If we have unanswered question then set first one to be the next question
    if (nextQuestion) {
      setCurrentStep(nextQuestion.id);
    } else {
      // If we have no questions left we assume we are done
      if (dispatch) {
        dispatch(setAnswersToQuestions(questions));
        dispatch(
          setFormViewState({
            currentStep: currentStep,
            questionnaire: questions,
            previousQuestionnaires: previousQuestionsUpdates,
          })
        );
        dispatch(setFormScene('DETAILS'));
      }
    }
  };

  return (
    <Layout viewId={'form'}>
      {questions.map((question) => {
        return (
          <Step
            key={question.id}
            isMemberOfAkassa={state.givenConsent !== undefined}
            question={question}
            currentStep={currentStep}
            onChange={handleOnChange}
            onComplementaryAnswersChange={handleOnComplementaryAnswersChange}
            onNext={handleOnNextClick}
            onPrevious={handleOnPreviousClick}
          />
        );
      })}
    </Layout>
  );
};
